import { useMemo } from "react";
import { flatten, uniqBy } from "lodash";
import { useInfiniteQuery } from "react-query";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";

export interface BotCommentRecord {
  body_trix_content: string | null;
  created_at: string;
  editor: "trix" | "tiptap";
  id: number;
  post_name?: string;
  post_slug: string;
  post_truncated_content?: string;
  space_slug: string;
  tiptap_body: object | null;
  updated_at: string;
}

interface PaginatedResponse {
  count: number;
  has_next_page: boolean;
  page: number;
  per_page: number;
  records: BotCommentRecord[];
}

export const useBotCommentsInfinite = ({
  profileId,
  enabled,
  perPage = 10,
}: {
  profileId?: number;
  enabled?: boolean;
  perPage?: number;
}) => {
  const queryKey = ["bot-comments-infinite", profileId, perPage];

  const fetchPage = (page: number) =>
    reactQueryGet(
      internalApi.communityBot.agents.comments({
        id: profileId,
        params: { page, per_page: perPage },
      }),
    );

  const infiniteQuery = useInfiniteQuery<PaginatedResponse, Error>(
    queryKey,
    ({ pageParam = 1 }) => fetchPage(pageParam),
    {
      enabled: Boolean(profileId) && Boolean(enabled),
      getNextPageParam: lastPage =>
        lastPage.has_next_page ? lastPage.page + 1 : undefined,
    },
  );

  const comments = useMemo(
    () =>
      infiniteQuery.isLoading || !infiniteQuery.data
        ? []
        : uniqBy(flatten(infiniteQuery.data.pages.map(p => p.records)), "id"),
    [infiniteQuery.data, infiniteQuery.isLoading],
  );

  return { ...infiniteQuery, comments };
};
